import moment, { Moment } from "moment-timezone"
import Papa from "papaparse"

export type ConfirmationUploadType = "MATERIAL" | "EMPTIES"

export const CONFIRMATION_UPLOAD_CSV_ERROR_MESSAGE_IDS = {
    unreadableRow: "transportOrder.confirmationUpload.result.unreadable",
    transportOrderNumberRequired: "transportOrder.confirmationUpload.error.transportOrderNumberRequired",
    shipFromDunsRequired: "transportOrder.confirmationUpload.error.shipFromDunsRequired",
    shipFromDunsInvalid: "transportOrder.confirmationUpload.error.shipFromDunsInvalid",
    carrierNameRequired: "transportOrder.confirmationUpload.error.carrierNameRequired",
    carrierDunsRequired: "transportOrder.confirmationUpload.error.carrierDunsRequired",
    carrierDunsInvalid: "transportOrder.confirmationUpload.error.carrierDunsInvalid",
    licensePlateRequired: "transportOrder.confirmationUpload.error.licensePlateRequired",
    licensePlateTooLong: "transportOrder.confirmationUpload.error.licensePlateTooLong",
    pickupTimeFromRequired: "transportOrder.confirmationUpload.error.pickupTimeFromRequired",
    pickupTimeFromInvalid: "transportOrder.confirmationUpload.error.pickupTimeFromInvalid",
    pickupTimeToRequired: "transportOrder.confirmationUpload.error.pickupTimeToRequired",
    pickupTimeToInvalid: "transportOrder.confirmationUpload.error.pickupTimeToInvalid",
    pickupTimeWindowInvalid: "transportOrder.confirmationUpload.error.pickupTimeWindowInvalid",
    transportChainReferenceTooLong:
        "transportOrder.confirmationUpload.error.transportChainReferenceTooLong",
    loadingAccessCodeInvalid: "transportOrder.confirmationUpload.error.loadingAccessCodeInvalid",
} as const

export type ConfirmationUploadCsvErrorMessageId =
    typeof CONFIRMATION_UPLOAD_CSV_ERROR_MESSAGE_IDS[
        keyof typeof CONFIRMATION_UPLOAD_CSV_ERROR_MESSAGE_IDS
    ]

export interface ParsedConfirmationRow {
    sourceLine: number
    transportOrderNumber: string
    shipFromDuns: string
    carrierName: string
    carrierDuns: string
    licensePlate: string
    pickupTimeFrom: string
    pickupTimeTo: string
    transportChainReference: string
    loadingAccessCode: string
}

const isEmptyRow = (row: readonly string[]): boolean => row.every(value => value === "")

export const parseConfirmationCsv = async (file: File): Promise<ParsedConfirmationRow[]> => {
    const content = await file.text()

    if (content.trim() === "") {
        return []
    }

    const result = Papa.parse<string[]>(content, {
        delimiter: ";",
        skipEmptyLines: false,
        transform: value => value.trim(),
    })

    if (result.errors.length > 0) {
        throw new Error("CSV file could not be parsed")
    }

    return result.data
        .map((row, index) => ({ row, index }))
        .slice(1)
        .filter(({ row }) => !isEmptyRow(row))
        .map(({ row, index }) => ({
            sourceLine: index + 1,
            transportOrderNumber: row[0] ?? "",
            shipFromDuns: row[1] ?? "",
            carrierName: row[9] ?? "",
            carrierDuns: row[10] ?? "",
            licensePlate: row[11] ?? "",
            pickupTimeFrom: row[14] ?? "",
            pickupTimeTo: row[15] ?? "",
            transportChainReference: row[17] ?? "",
            loadingAccessCode: row[18] ?? "",
        }))
}

const LOADING_ACCESS_CODE = /^\d{1,15}$/u
const DUNS = /^\d{9}$/u

const parseTimestamp = (value: string): Moment | null => {
    const parsed = moment(value, "YYYYMMDDHHmm", true)
    return parsed.isValid() ? parsed : null
}

export const validateConfirmationRow = (
    row: ParsedConfirmationRow,
    uploadType: ConfirmationUploadType
): ConfirmationUploadCsvErrorMessageId | undefined => {
    if (row.transportOrderNumber === "") {
        return CONFIRMATION_UPLOAD_CSV_ERROR_MESSAGE_IDS.transportOrderNumberRequired
    }
    if (row.shipFromDuns === "" && uploadType === "MATERIAL") {
        return CONFIRMATION_UPLOAD_CSV_ERROR_MESSAGE_IDS.shipFromDunsRequired
    }
    if (row.shipFromDuns !== "" && !DUNS.test(row.shipFromDuns)) {
        return CONFIRMATION_UPLOAD_CSV_ERROR_MESSAGE_IDS.shipFromDunsInvalid
    }
    if (row.carrierName === "") {
        return CONFIRMATION_UPLOAD_CSV_ERROR_MESSAGE_IDS.carrierNameRequired
    }
    if (row.carrierDuns === "") {
        return CONFIRMATION_UPLOAD_CSV_ERROR_MESSAGE_IDS.carrierDunsRequired
    }
    if (!DUNS.test(row.carrierDuns)) {
        return CONFIRMATION_UPLOAD_CSV_ERROR_MESSAGE_IDS.carrierDunsInvalid
    }
    if (row.licensePlate === "") {
        return CONFIRMATION_UPLOAD_CSV_ERROR_MESSAGE_IDS.licensePlateRequired
    }
    if (row.licensePlate.length > 35) {
        return CONFIRMATION_UPLOAD_CSV_ERROR_MESSAGE_IDS.licensePlateTooLong
    }

    if (row.pickupTimeFrom === "") {
        return CONFIRMATION_UPLOAD_CSV_ERROR_MESSAGE_IDS.pickupTimeFromRequired
    }
    const pickupTimeFrom = parseTimestamp(row.pickupTimeFrom)
    if (pickupTimeFrom === null) {
        return CONFIRMATION_UPLOAD_CSV_ERROR_MESSAGE_IDS.pickupTimeFromInvalid
    }

    if (row.pickupTimeTo === "") {
        return CONFIRMATION_UPLOAD_CSV_ERROR_MESSAGE_IDS.pickupTimeToRequired
    }
    const pickupTimeTo = parseTimestamp(row.pickupTimeTo)
    if (pickupTimeTo === null) {
        return CONFIRMATION_UPLOAD_CSV_ERROR_MESSAGE_IDS.pickupTimeToInvalid
    }
    if (pickupTimeTo.isBefore(pickupTimeFrom)) {
        return CONFIRMATION_UPLOAD_CSV_ERROR_MESSAGE_IDS.pickupTimeWindowInvalid
    }

    if (row.transportChainReference.length > 18) {
        return CONFIRMATION_UPLOAD_CSV_ERROR_MESSAGE_IDS.transportChainReferenceTooLong
    }

    if (row.loadingAccessCode !== "" && !LOADING_ACCESS_CODE.test(row.loadingAccessCode)) {
        return CONFIRMATION_UPLOAD_CSV_ERROR_MESSAGE_IDS.loadingAccessCodeInvalid
    }

    return undefined
}
